import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import {
  AnchorOutlined,
  CategoryOutlined,
  DirectionsBoatOutlined,
  FlagOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { useCustomMutation, useNotification, useParsed } from "@refinedev/core";
import { useForm } from "@refinedev/react-hook-form";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  AdminOrderDto,
  OrderRfq,
  OrderStatus,
} from "../../apiClient/generated";
import { EditView } from "../../components/EditView";
import { Info } from "../../components/ports/info-card";
import { useAllowableStatusSwitch } from "../../hooks/useAllowableStatusSwitch";
import { orderStatusParser } from "../../lib/orderStatusParser";
import { SectionBox } from "../ports/edit";

const rfqColumns: GridColDef<OrderRfq>[] = [
  { field: "id", headerName: "ID" },
  { field: "origin", headerName: "Origin" },
  { field: "coveringDays", headerName: "Covering Days" },
  { field: "contractCurrency", headerName: "Contract currency" },
  { field: "wbPono", headerName: "wbPono" },
  { field: "comment", headerName: "Comment" },
  { field: "deliveryDate", headerName: "Delivery Date" },
  { field: "departureDate", headerName: "Departure Date" },
  { field: "ssPoNo", headerName: "SS PO NO" },
  { field: "created", headerName: "Created Date" },
  { field: "lastModified", headerName: "Last Modified" },
  {
    field: "deliveryPort",
    headerName: "Delivery Port",
    renderCell: ({ row: rfq }) => rfq.deliveryPort.portName,
  },
  {
    field: "nextPort",
    headerName: "Next Port",
    renderCell: ({ row: rfq }) => rfq.nextPort.portName,
  },
  {
    field: "agent",
    headerName: "Agent",
    renderCell: ({ row: rfq }) => rfq.agent.name,
  },
  {
    field: "supplier",
    headerName: "Supplier",
    renderCell: ({ row: rfq }) => rfq.supplier.name,
  },
];

export const EditOrder = () => {
  let { id } = useParsed();

  const {
    refineCore: { formLoading, query },
  } = useForm<AdminOrderDto>({
    refineCoreProps: {
      action: "edit",
      resource: "AdminOrders",
      id,
      redirect: false,
    },
  });

  const order = query?.data?.data;
  const rfqs = order?.rfqs ? [order?.rfqs] : []; // TODO: Replace this when RFQs is an Array again

  const [deliveryDate, setDeliveryDate] = useState<string | undefined>();
  const [originalDeliveryDate, setOriginalDeliveryDate] = useState<
    string | undefined
  >();

  const { mutate } = useCustomMutation();

  const { displayedStatus } = orderStatusParser(order);

  const { possibleSwitch } = useAllowableStatusSwitch(order?.status);
  const [updatedStatus, setUpdatedStatus] = useState<OrderStatus | undefined>();

  useEffect(() => {
    setUpdatedStatus(order?.status);
    let original = dayjs(order?.rfqs?.deliveryDate).format("YYYY-MM-DD");
    setDeliveryDate(original);
    setOriginalDeliveryDate(original);
  }, [order?.status]);

  const { open } = useNotification();

  const changeDeliveryDate = () => {
    mutate(
      {
        url: `${import.meta.env.VITE_API_URL}/api/odata/AdminOrders/${
          order?.orderId
        }/UpdateDeliveryDate`,
        method: "post",
        values: {
          DeliveryDate: dayjs(deliveryDate).format("YYYY-MM-DD"),
        },
      },
      {
        onSuccess: () => {
          query?.refetch();
          open?.({
            type: "success",
            message: "Delivery date changed",
            description: "The delivery date has successfully been changed.",
          });
        },
      }
    );
  };

  const changeStatus = () => {
    const statusString = Object.entries(OrderStatus).find(
      ([, val]) => val === updatedStatus
    )?.[0];

    mutate(
      {
        url: `${import.meta.env.VITE_API_URL}/api/odata/AdminOrders/${
          order?.orderId
        }/Order.SetStatus`,
        method: "post",
        values: {
          Status: statusString,
        },
      },
      {
        onSuccess: () => {
          query?.refetch();
          open?.({
            type: "success",
            message: "Status updated",
            description: "The status was updated",
          });
        },
      }
    );
  };

  const isChangingDeliveryDateUnallowed =
    formLoading || (order?.status != null && ![3, 5, 6, 7].includes(order.status));

  // Disable if delivery date is unchanged or changing is not allowed
  const isChangeDeliveryDateDisabled =
    isChangingDeliveryDateUnallowed || deliveryDate === originalDeliveryDate;

  return (
    <EditView name="Order">
      <Grid2 size={{ xs: 12, lg: 8 }}>
        <SectionBox>
          <Info
            icon={<DirectionsBoatOutlined />}
            label={"ID"}
            value={order?.orderId}
          />

          <Divider />

          <Info
            icon={<AnchorOutlined />}
            label={"Vessel IMO"}
            value={order?.vesselImo}
          />

          <Divider />

          <Info
            icon={<ReceiptLongOutlined />}
            label={"Wb Pono"}
            value={order?.wbPono}
          />

          <Divider />

          <Info
            icon={<CategoryOutlined />}
            label={"Type"}
            value={order?.type}
          />

          <Divider />

          <Info
            icon={<FlagOutlined />}
            label={"Status"}
            value={displayedStatus}
          />
        </SectionBox>
      </Grid2>

      <Grid2 size={{ xs: 12, lg: 4 }} container direction="column" spacing={2}>
        <Box
          sx={{
            p: 2,
            outline: "1px solid",
            outlineColor: "divider",
            borderRadius: 1,
            boxShadow: "none",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6">Delivery Date</Typography>
            <Stack spacing={1}>
              <DatePicker
                label="Delivery date"
                disabled={isChangingDeliveryDateUnallowed}
                value={deliveryDate ? dayjs(deliveryDate) : undefined}
                onChange={(date) =>
                  setDeliveryDate(dayjs(date).format("YYYY-MM-DD"))
                }
                // minDate={dayjs(order?.rfqs?.departureDate)}
              />
              <Button
                variant="outlined"
                sx={{ mt: 4 }}
                onClick={() => changeDeliveryDate()}
                disabled={isChangeDeliveryDateDisabled}
              >
                Change Delivery Date
              </Button>

              {!formLoading && isChangingDeliveryDateUnallowed && (
                <Typography color="text.secondary">
                  It is not possible to change delivery date of this order.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>
        <Box
          sx={{
            p: 2,
            outline: "1px solid",
            outlineColor: "divider",
            boxShadow: "none",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6">Status</Typography>
            <Stack spacing={1}>
              <FormControl fullWidth>
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  id="status-select"
                  value={updatedStatus ?? ""}
                  label="Status"
                  onChange={(e) =>
                    setUpdatedStatus(e.target.value as OrderStatus)
                  }
                >
                  {Object.entries(OrderStatus)
                    .filter(([, status]) => !isNaN(Number(status)))
                    .map(([text, status]) => {
                      const allowed = possibleSwitch.find(
                        (item) => item === status
                      );
                      if (status != order?.status && !allowed) return;

                      return (
                        <MenuItem key={status} value={status}>
                          {text} ({status})
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                disabled={
                  formLoading ||
                  updatedStatus == undefined ||
                  updatedStatus == order?.status
                }
                onClick={() => changeStatus()}
              >
                Change Status
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Grid2>

      <Grid2
        container
        spacing={2}
        sx={{ width: "100%" }}
        columns={{ xs: 4, sm: 8, lg: 4 }}
      >
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            RFQs
          </Typography>

          <DataGrid
            columns={rfqColumns}
            rows={rfqs}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
          />
        </Stack>
      </Grid2>
    </EditView>
  );
};
